import { ChangeDetectionStrategy, Component, inject, viewChild } from '@angular/core';

import { DEV_SCENARIO_COOKIE_NAME, devScenarioNames } from '@arya/shared/domain';
import { CookieStore } from '@arya/shared/infrastructure/cookie';

import { NoxoStatusChip } from '@noxo/ui/badge';
import { NoxoButton } from '@noxo/ui/button';
import { NoxoIcon } from '@noxo/ui/icon';

import { DemoAuthenticator } from './demo-authenticator';
import { DemoInbox } from './demo-inbox';
import { DemoOutbox } from './demo-outbox';
import { DemoPanel } from './demo-panel';
import { DemoScenarios } from './demo-scenarios';

@Component({
  selector: 'noxo-demo-tools',
  imports: [DemoAuthenticator, DemoInbox, DemoPanel, DemoScenarios, NoxoButton, NoxoIcon, NoxoStatusChip],
  template: `
    <button
      type="button"
      noxo-button="secondary"
      size="s"
      class="fab"
      title="Alt+I opens it even over a dialog"
      (click)="inbox.open()">
      <noxo-icon icon="envelope" />
      Inbox
      @if (unseenCode(); as code) {
        <noxo-status-chip tone="success">{{ code }}</noxo-status-chip>
      }
    </button>
    <button
      type="button"
      noxo-button="secondary"
      size="s"
      class="fab"
      title="Alt+C opens it even over a dialog"
      (click)="codes.open()">
      <noxo-icon icon="mobile-screen-button" />
      Codes
    </button>
    <button
      type="button"
      noxo-button="secondary"
      size="s"
      class="fab"
      title="Alt+S opens it even over a dialog"
      (click)="scenarios.open()">
      <noxo-icon icon="flask" />
      Scenarios
      @if (activeScenarios > 0) {
        <noxo-status-chip tone="alert">{{ activeScenarios }} on</noxo-status-chip>
      }
    </button>
    <noxo-demo-panel
      #inbox
      heading="Inbox"
      caption="E-mails and SMS the app sent"
      (dismissed)="markSeen()">
      @if (inbox.isOpen()) {
        <noxo-demo-inbox />
      }
    </noxo-demo-panel>
    <noxo-demo-panel
      #codes
      heading="Authenticator"
      caption="Codes the demo authenticator app shows right now">
      @if (codes.isOpen()) {
        <noxo-demo-authenticator />
      }
    </noxo-demo-panel>
    <noxo-demo-panel
      #scenarios
      heading="Scenarios"
      caption="Make the API fail or answer an edge case">
      @if (scenarios.isOpen()) {
        <noxo-demo-scenarios />
      }
    </noxo-demo-panel>
  `,
  styleUrls: ['./demo-contents.scss', './demo-fab.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown)': 'openFromKeyboard($event)' },
})
export class DemoTools {
  private readonly outbox = inject(DemoOutbox);

  protected readonly activeScenarios = devScenarioNames(
    `${DEV_SCENARIO_COOKIE_NAME}=${inject(CookieStore).get(DEV_SCENARIO_COOKIE_NAME) ?? ''}`
  ).length;

  protected readonly unseenCode = this.outbox.unseenCode;

  private readonly inboxPanel = viewChild.required<DemoPanel>('inbox');
  private readonly codesPanel = viewChild.required<DemoPanel>('codes');
  private readonly scenariosPanel = viewChild.required<DemoPanel>('scenarios');
  private readonly shortcuts: Readonly<Record<string, () => DemoPanel>> = {
    KeyI: this.inboxPanel,
    KeyC: this.codesPanel,
    KeyS: this.scenariosPanel,
  };

  protected openFromKeyboard(event: KeyboardEvent): void {
    const panel = event.altKey && !event.ctrlKey && !event.metaKey ? this.shortcuts[event.code]?.() : undefined;
    if (panel === undefined) return;
    event.preventDefault();
    if (panel.isOpen()) panel.close();
    else panel.open();
  }

  protected markSeen(): void {
    this.outbox.markSeen();
  }
}
